/**
 * Where the keyboard goes when the Przegląd's open task card leaves — and where it goes back to
 * when the write that was to take it away is refused.
 *
 * ── Every write on the card can take the card away ──────────────────────────────────────────
 * „Gotowe" and „Później" remove the task at once (the responder is optimistic). „Oznacz jako
 * przejrzane" and every act inside the card — „Dodaj tydzień", „Zakończ plan", „Przedłuż",
 * „Ukończ cel"… — invalidate the queue, and the read that follows removes the task when the write
 * made it stop being true. Either way the pressed control goes with the card, so without a hand-off
 * the keyboard falls to `<body>` and the next Tab restarts at the skip link — the Pulpit's defect,
 * which `pages/trainer-home/lib/useQueueCard` fixes the same way.
 *
 * Where the next landing already exists, the ANSWER moves the keyboard itself: with two tasks left
 * the strip stays, so the next task's tab takes it and that task opens. Everywhere else the landing
 * waits for the render that removes the task — one left (no strip, a new card) or none (the
 * up-to-date line) — and then takes the keyboard only from `<body>`, never from wherever the
 * trainer has moved to.
 *
 * ── A landing is never left armed ────────────────────────────────────────────────────────────
 * An armed landing is a promise to move the keyboard when that task leaves, so one outliving its
 * write would take the keyboard later, off a render nothing the trainer did caused. It is disarmed
 * three ways: the task left (the landing is spent); the write was refused (the task stays); or the
 * write was accepted and a queue read that arrived AFTER it still carries the task (the write did
 * not resolve it — `queueUpdatedAt` is what dates that read).
 *
 * ── A refused answer brings the keyboard back ────────────────────────────────────────────────
 * The responder puts a refused task back where it was, and the keyboard belongs on it again: its
 * card is opened and its own „Gotowe" or „Później" takes the focus — asked, like the Pulpit, only
 * of a keyboard still where the press put it or on `<body>`. An in-card act and „Oznacz jako
 * przejrzane" never removed anything, so their pressed control is still there and nothing moves.
 */

import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

import {
  QUEUE_CONTROL_ATTRIBUTE,
  taliaLayout,
  type AttentionItem,
  type AttentionResponseKind,
  type AttentionResponseOutcome,
} from '~/features/attention-queue';
import { tabButtonId } from '~/shared/ui/TabStrip';

const NO_SCROLL = { preventScroll: true } as const;

/** A task whose card may be leaving, with the keyboard owed somewhere when it does. */
interface Landing {
  readonly key: string;
  /** The answer pressed, so a refusal can return to that control; `null` for any other write. */
  readonly answer: AttentionResponseKind | null;
  /** The keyboard has been handed on — in the press, or on the render that removed the task. */
  spent: boolean;
  /** Where the keyboard was put. A refusal takes it back only from here or from `<body>`. */
  placed: Element | null;
  /** When a write other than an answer was accepted — see "never left armed" in the header. */
  acceptedAt: number | null;
}

export interface TaliaLandingDeps {
  readonly tasks: readonly AttentionItem[];
  /** The open card's task. */
  readonly selected: AttentionItem | null;
  /** When the queue read the tasks come from last answered. */
  readonly queueUpdatedAt: number;
  /** The responder's settled outcome — see `useAttentionResponse`. */
  readonly outcome: AttentionResponseOutcome | null;
  /** Open a task's card. */
  readonly choose: (key: string) => void;
  readonly headingRef: RefObject<HTMLSpanElement | null>;
  readonly upToDateRef: RefObject<HTMLDivElement | null>;
}

export interface TaliaLanding {
  /** „Gotowe" or „Później" is pressed on `item`; call BEFORE the response is sent. */
  readonly answering: (item: AttentionItem, kind: AttentionResponseKind) => void;
  /** Any other write on the open card is starting. */
  readonly writing: (key: string) => void;
  /** That write was accepted; the next read decides whether the task leaves. */
  readonly accepted: (key: string) => void;
  /** That write was refused; the task and its control stay. */
  readonly refused: (key: string) => void;
}

export function useTaliaLanding({
  tasks,
  selected,
  queueUpdatedAt,
  outcome,
  choose,
  headingRef,
  upToDateRef,
}: TaliaLandingDeps): TaliaLanding {
  /** Refs, written in presses and callbacks and read after commits — never in render. */
  const landing = useRef<Landing | null>(null);
  /** A refused answer's task and control, focused once its card is drawn again. */
  const returning = useRef<{ readonly key: string; readonly answer: AttentionResponseKind } | null>(
    null,
  );

  useLayoutEffect(() => {
    const armed = landing.current;
    if (armed !== null) {
      const present = tasks.some((item) => item.key === armed.key);
      if (!present && !armed.spent) {
        armed.spent = true;
        const active = document.activeElement;
        if (active === null || active === document.body) armed.placed = landNext();
        // An answer stays on record until the server settles it; nothing else can come back.
        if (armed.answer === null) landing.current = null;
      } else if (present && armed.acceptedAt !== null && queueUpdatedAt > armed.acceptedAt) {
        // Accepted, and a read from after it still carries the task: this write did not resolve it.
        landing.current = null;
      }
    }

    const back = returning.current;
    if (back !== null && selected?.key === back.key) {
      returning.current = null;
      headingRef.current
        ?.closest('article')
        ?.querySelector<HTMLElement>(`[${QUEUE_CONTROL_ATTRIBUTE}="${back.answer}"]`)
        ?.focus(NO_SCROLL);
    }
  });

  useEffect(() => {
    const armed = landing.current;
    if (armed === null || armed.answer === null || outcome === null || outcome.key !== armed.key) {
      return;
    }
    landing.current = null;
    if (outcome.ok) return;
    const active = document.activeElement;
    if (active !== armed.placed && active !== null && active !== document.body) return;
    returning.current = { key: armed.key, answer: armed.answer };
    choose(armed.key);
  }, [outcome, choose]);

  return {
    answering: (item, kind) => {
      returning.current = null;
      const remaining = tasks.filter((entry) => entry.key !== item.key);
      const index = tasks.findIndex((entry) => entry.key === item.key);
      const next = remaining[Math.min(index, remaining.length - 1)];
      // With a strip still standing after this answer, its next tab already exists: take it now.
      const nextTab =
        taliaLayout(remaining.length) === 'tabs' && next !== undefined
          ? document.getElementById(tabButtonId(next.key))
          : null;
      if (nextTab !== null && next !== undefined) {
        nextTab.focus(NO_SCROLL);
        choose(next.key);
      }
      landing.current = {
        key: item.key,
        answer: kind,
        spent: nextTab !== null,
        placed: nextTab,
        acceptedAt: null,
      };
    },
    writing: (key) => {
      returning.current = null;
      landing.current = { key, answer: null, spent: false, placed: null, acceptedAt: null };
    },
    accepted: (key) => {
      if (landing.current?.key === key) landing.current.acceptedAt = Date.now();
    },
    refused: (key) => {
      if (landing.current?.key === key) landing.current = null;
    },
  };

  /** Hand the keyboard to whatever now stands where the card was, and say where it went. */
  function landNext(): Element | null {
    const layout = taliaLayout(tasks.length);
    const target =
      layout === 'tabs' && selected !== null
        ? document.getElementById(tabButtonId(selected.key))
        : layout === 'single'
          ? headingRef.current
          : upToDateRef.current;
    target?.focus(NO_SCROLL);
    return target;
  }
}
